import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import { serviceurl } from 'src/app/constant/constant';
import { SKIP_ERROR_TOAST } from 'src/app/interceptors/error.interceptor';
import { Cart, CouponPreview } from './marketplace.models';
import { AuthTokenService } from 'src/app/services/auth-token.service';

/** The discount code the buyer applied in the cart, carried to checkout. */
const COUPON_KEY = 'wt_cart_coupon';

@Injectable({ providedIn: 'root' })
export class CartService {
  private apiUrl = serviceurl + 'api/v1/cart';

  /**
   * Live item count for the header badge. Every endpoint below returns the
   * whole cart, so each call refreshes this — the header subscribes once and
   * stays in sync without re-fetching.
   */
  private readonly countSubject = new BehaviorSubject<number>(0);
  readonly count$ = this.countSubject.asObservable();

  constructor(private http: HttpClient, private authTokens: AuthTokenService) {}

  /** What `code` takes off the cart right now (the cart page shows any error itself). */
  previewCoupon(code: string): Observable<CouponPreview> {
    return this.http.post<CouponPreview>(`${this.apiUrl}/coupon/`, { code }, {
      context: new HttpContext().set(SKIP_ERROR_TOAST, true),
    });
  }

  get appliedCoupon(): string | null {
    try {
      return sessionStorage.getItem(COUPON_KEY);
    } catch {
      return null;
    }
  }

  set appliedCoupon(code: string | null) {
    try {
      if (code) sessionStorage.setItem(COUPON_KEY, code);
      else sessionStorage.removeItem(COUPON_KEY);
    } catch {
      /* storage unavailable: the code just isn't remembered */
    }
  }

  getCart(): Observable<Cart> {
    return this.http.get<Cart>(`${this.apiUrl}/`).pipe(tap((cart) => this.publish(cart)));
  }

  /**
   * Header-safe refresh for the badge.
   *
   * Bails unless the marketplace JWT is actually present. `currentUser` lives
   * in localStorage but the token lives in sessionStorage, so the two desync
   * routinely (new tab, cleared session, preview mode off) — and a 401 here is
   * not recoverable locally: ErrorInterceptor sits earlier in the chain and
   * redirects the whole app to /login before any catchError here could run.
   * Sending the request only when it can succeed is the fix; the catchError
   * below is just belt-and-braces for network failures.
   */
  refreshCount(): void {
    if (!this.authTokens.hasSession) {
      this.countSubject.next(0);
      return;
    }
    this.http
      .get<Cart>(`${this.apiUrl}/`)
      .pipe(catchError(() => of(null)))
      .subscribe((cart) => this.publish(cart));
  }

  addLine(listingId: number, quantity = 1): Observable<Cart> {
    return this.http
      .post<Cart>(`${this.apiUrl}/lines/`, { listing: listingId, quantity })
      .pipe(tap((cart) => this.publish(cart)));
  }

  updateQuantity(lineId: number, quantity: number): Observable<Cart> {
    return this.http
      .patch<Cart>(`${this.apiUrl}/lines/${lineId}/`, { quantity })
      .pipe(tap((cart) => this.publish(cart)));
  }

  removeLine(lineId: number): Observable<Cart> {
    return this.http
      .delete<Cart>(`${this.apiUrl}/lines/${lineId}/`)
      .pipe(tap((cart) => this.publish(cart)));
  }

  toggleSaveForLater(lineId: number): Observable<Cart> {
    return this.http
      .post<Cart>(`${this.apiUrl}/lines/${lineId}/save-for-later/`, {})
      .pipe(tap((cart) => this.publish(cart)));
  }

  /** Cleared on logout so the next user doesn't inherit a stale badge. */
  clearCount(): void {
    this.countSubject.next(0);
  }

  private publish(cart: Cart | null): void {
    if (!cart) {
      this.countSubject.next(0);
      return;
    }
    // Prefer the server's count; fall back to summing active lines when a
    // response predates that field.
    const count =
      typeof cart.item_count === 'number'
        ? cart.item_count
        : (cart.lines || []).filter((l) => !l.saved_for_later).reduce((n, l) => n + (l.quantity || 0), 0);
    this.countSubject.next(count);
  }
}
